// Web Speech API wrapper.
//
// Phase 0 / MVP — free, $0/mo. Works inside iPad Safari tabs and (per spike-1
// findings) usually inside installed PWAs too. If the spike shows
// `webkitSpeechRecognition` is undefined in standalone display mode on the
// user's iOS version, we'll need to wrap in Capacitor — see plan §7.

export interface RecognitionResult {
  transcript: string;
  confidence: number;
  alternatives: string[];
  latencyMs: number;       // measured from release → result
}

export class WebSpeechRecognizer {
  private SR: typeof SpeechRecognition | null;
  private active: SpeechRecognition | null = null;
  private pressedAt = 0;
  private releasedAt = 0;
  private resolver: ((r: RecognitionResult | null) => void) | null = null;
  readonly supported: boolean;

  constructor() {
    this.SR = (window.SpeechRecognition || window.webkitSpeechRecognition) ?? null;
    this.supported = !!this.SR;
  }

  // Begin listening. Resolves with the recognized result on `stop()`, or null on error.
  start(lang: string = 'zh-CN'): Promise<RecognitionResult | null> {
    if (!this.SR) return Promise.resolve(null);
    if (this.active) this.stop(); // safety

    return new Promise<RecognitionResult | null>((resolve) => {
      this.resolver = resolve;
      this.pressedAt = performance.now();

      const rec = new this.SR!();
      rec.lang = lang;
      rec.continuous = false;
      rec.interimResults = false;
      rec.maxAlternatives = 3;

      let resolved = false;
      const finish = (r: RecognitionResult | null) => {
        if (resolved) return;
        resolved = true;
        this.resolver?.(r);
        this.resolver = null;
        this.active = null;
      };

      rec.onresult = (e: SpeechRecognitionEvent) => {
        const result = e.results[0];
        if (!result) return finish(null);
        const top = result[0];
        const alts: string[] = [];
        for (let i = 0; i < result.length; i++) alts.push(result[i].transcript);
        finish({
          transcript: top.transcript ?? '',
          confidence: top.confidence ?? 0,
          alternatives: alts,
          latencyMs: Math.round(performance.now() - this.releasedAt),
        });
      };
      rec.onerror = () => finish(null);
      rec.onend = () => finish(null); // fallback if onresult never fires

      try {
        rec.start();
        this.active = rec;
      } catch {
        finish(null);
      }
    });
  }

  stop(): void {
    if (!this.active) return;
    this.releasedAt = performance.now();
    try { this.active.stop(); } catch { /* swallow */ }
  }

  cancel(): void {
    if (!this.active) return;
    try { this.active.abort(); } catch { /* swallow */ }
    this.active = null;
    this.resolver?.(null);
    this.resolver = null;
  }
}

// Play TTS via the iPad's built-in Mandarin voice. Falls back silently if
// no zh voice is available. Used for model-audio playback in the failure
// scaffold when the parent hasn't recorded their own audio for a card.
export function speakHanzi(text: string, rate: number = 0.85): void {
  if (!('speechSynthesis' in window)) return;
  const u = new SpeechSynthesisUtterance(text);
  u.lang = 'zh-CN';
  const voice = speechSynthesis.getVoices().find((v) => v.lang?.toLowerCase().startsWith('zh'));
  if (voice) u.voice = voice;
  u.rate = rate;
  // Cancel any current utterance so rapid replay doesn't queue up.
  speechSynthesis.cancel();
  speechSynthesis.speak(u);
}
